import { Option } from "@inrev/core";
import {
	failureCopy,
	formatReportDate,
	type IndividualCreditReport,
} from "@portal/api/account/admin/individualCreditReport";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Banner, Icon, Tag } from "@portal/components/primitives";
import type { FC } from "react";
import { match } from "ts-pattern";
import {
	ExperianReportBody,
	FailedReportBody,
	LegacyReportBody,
	NoReportBody,
	type RepullControl,
} from "./IndividualCreditReportStates";

export type IndividualCreditReportModalProps = {
	name: string;
	report: IndividualCreditReport | undefined;
	/** A newer pull that failed while `report`, an older success, stays in use for underwriting. */
	failedAttempt?: IndividualCreditReport;
	onClose: () => void;
	/** Runs a fresh pull from the failed, legacy and empty states. */
	onRepull: () => void;
	repullPending: boolean;
	repullFailed: boolean;
};

export const IndividualCreditReportModal: FC<IndividualCreditReportModalProps> = ({
	name,
	report,
	failedAttempt,
	onClose,
	onRepull,
	repullPending,
	repullFailed,
}) => {
	const repull: RepullControl = {
		subject: name,
		run: onRepull,
		pending: repullPending,
		failed: repullFailed,
	};
	const legacy = report?.vendor === "meridian" && report.outcome.status === "success";

	return (
		<Modal onClickOutside={onClose} closeOnEscape returnFocus itemClassName="w-full">
			<div className="contents" onWheel={(e) => e.stopPropagation()}>
				<ModalItemWithHeader
					className="amp-mask max-w-[min(900px,92vw)] w-full max-h-[min(85vh,900px)] flex flex-col"
					headerClassName="items-center"
					bodyClassName="overflow-y-auto overscroll-contain min-h-0"
					header={
						<div
							className="flex items-center gap-2.5"
							data-testid="individual_credit_report_header"
						>
							{name}
							{legacy && <Tag color="gray">Legacy report</Tag>}
						</div>
					}
					onClose={onClose}
				>
					<div
						className="flex flex-col gap-4 px-6 pb-7 pt-6"
						data-testid="individual_credit_report_modal_content"
					>
						{failedAttempt?.outcome.status === "failed" && Option.isSome(report) && (
							<Banner tone="notification" data-testid="individual_credit_failed_attempt">
								<Banner.Leading>
									<Icon type="exclamation-triangle" color="warning" size="small" />
								</Banner.Leading>
								<Banner.Content>
									The pull on {formatReportDate(failedAttempt.createdAt)} failed (
									{failureCopy(failedAttempt.outcome.failure, failedAttempt.vendor).label}). This is
									the report from {formatReportDate(report.createdAt)}, which underwriting still
									uses.
								</Banner.Content>
							</Banner>
						)}
						{match(report)
							.with(undefined, () => <NoReportBody onClose={onClose} repull={repull} />)
							.with({ outcome: { status: "failed" } }, (failed) => (
								<FailedReportBody
									report={failed}
									failure={failed.outcome.failure}
									onClose={onClose}
									repull={repull}
								/>
							))
							.with({ vendor: "experian", outcome: { status: "success" } }, (experian) => (
								<ExperianReportBody report={experian} outcome={experian.outcome} />
							))
							.with({ vendor: "meridian", outcome: { status: "success" } }, (meridian) => (
								<LegacyReportBody
									report={meridian}
									outcome={meridian.outcome}
									onClose={onClose}
									repull={repull}
								/>
							))
							.exhaustive()}
					</div>
				</ModalItemWithHeader>
			</div>
		</Modal>
	);
};
